import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Pagination, randomString } from "@/helpers/helperFunctions";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { toast } from "@/components/ui/use-toast";
import ViewModal from "@/components/blocks/flow/StrategyViewModal";
import ViewImageModal from "@/sections/build/components/ViewImageModal";
import { useGetSymbolData } from "@/api/symbolApi";
import { usePostCopyStrategy } from "@/api/strategyApi";
import SymbolUpdateModal from "@/components/common/SymbolUpdateModal";
import StrategyCard from "../cards/StrategyCard";
import SortStrategies from "./SortStrategies";
import { useUserContext } from "@/contexts/userContext";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import StrategyCardSmall from "../cards/StrategyCardSmall";

const StrategiesMarketplace = ({
  data,
  logos,
  totalDataLength,
  page,
  setPage,
  dataSize,
  filters,
  buildPage,
  setFilters,
  isPopup,
}: any) => {
  const { t } = useTranslation("marketplace-page");
  const { user } = useUserContext();
  const router = useRouter();
  const [symbol, setSymbol] = useState<string | undefined>(undefined);
  const { slug } = router.query;
  const { details } = useAppDetails();
  useEffect(() => {
    if (slug) {
      setSymbol(Array.isArray(slug) ? slug[0] : slug);
    }
  }, [slug]);

  const [currentCopyStrategy, setCurrentCopyStrategy] = useState<any>(null);
  const [selected, setSelected] = useState<any>({});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showSymbolUpdateModal, setShowSymbolUpdateModal] =
    useState<boolean>(false);

  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
    isPending: isCopyPending,
  } = usePostCopyStrategy();

  const { mutateAsync: getSymbolData, isPending: isCurrentPriceLoading } =
    useGetSymbolData();

  useEffect(() => {
    if (isCopySuccess && copyStrategyData && user) {
      toast({
        title: t("Strategy copied successfully"),
        variant: "default",
      });
      setTimeout(() => {
        router.push(
          "/edit-strategy/" +
            encodeURIComponent(user?.user_id) +
            "/" +
            copyStrategyData?.data?.id +
            randomString(5) +
            "/" +
            user.token +
            "/false" +
            `?ref=${router.asPath}&locale=${router.query.locale || "en"}`,
        );
      }, 2000);
    }
  }, [isCopySuccess, isCopyPending]);

  const copyHandler = async (item: any) => {
    if (!user) {
      toast({
        title: t("Login Required"),
        description: t("Please log in to copy this strategy."),
        variant: "destructive",
      });
      return;
    }
    try {
      const res = await getSymbolData({
        user_id: user?.user_id,
        ticker: item?.strategy.symbol,
        broker_id: details?.isWhiteLabled
          ? details?.id
          : item?.strategy.broker_id,
      });
      if (!res?.data || res?.data?.regularMarketPrice == 0) {
        setShowSymbolUpdateModal(true);
        setCurrentCopyStrategy(item?.strategy);
      } else {
        await copyStrategy({
          userId: user?.user_id,
          key: item?.strategy.keygen,
          symbol: item?.strategy.symbol,
        });
      }
    } catch (error) {
      toast({
        title: t("Error copying strategy"),
        variant: "destructive",
      });
    }
  };

  let [showImage, setShowImage] = useState(false);
  let [imageLink, setImageLink] = useState("");

  return (
    <div className="flex  justify-between flex-col md:flex-row gap-4 w-full h-full">
      <ViewModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        selected={selected}
        token={user?.token}
      />
      <ViewImageModal
        isOpen={showImage}
        onRequestClose={() => setShowImage(false)}
        link={imageLink}
      />
      <SymbolUpdateModal
        isOpen={showSymbolUpdateModal}
        strategy={currentCopyStrategy}
        setIsOpen={setShowSymbolUpdateModal}
      ></SymbolUpdateModal>
      <Card className={`!w-full  h-full ${buildPage && "border-none p-0 m-0"}`}>
        {!buildPage && (
          <CardHeader className="flex !p-3 px-6 justify-between flex-row items-center w-full">
            <CardTitle className="text-xl flex gap-2 items-center text-nowrap ">
              <h1>
                {t("Top Strategies")}
                {filters?.symbol || symbol
                  ? ` for ${symbol || filters.symbol}`
                  : ""}
              </h1>
            </CardTitle>
            <SortStrategies filters={filters} setFilters={setFilters} />
          </CardHeader>
        )}

        <CardContent className={`p-3 pt-0 w-full`}>
          <div
            className={`${data.length < 10 ? "h-auto" : "h-[2560px]"} w-full`}
          >
            {data?.map((item: any, index: number) => {
              return isPopup ? (
                <StrategyCardSmall
                  key={index}
                  item={item}
                  index={index}
                  logos={logos}
                  isCurrentPriceLoading={isCurrentPriceLoading}
                  isLoadingTicker={false}
                  showExplain={page <= 3}
                  onView={(val) => {
                    setSelected(val);
                    setShowModal(true);
                  }}
                  onExplain={(val) => {
                    setSelected(val);
                    setShowModal(true);
                  }}
                  onCopy={copyHandler}
                />
              ) : (
                <StrategyCard
                  key={index}
                  item={item}
                  index={index}
                  logos={logos}
                  isCurrentPriceLoading={isCurrentPriceLoading}
                  isLoadingTicker={false}
                  showExplain={page <= 3}
                  onView={(val) => {
                    setSelected(val);
                    setShowModal(true);
                  }}
                  onExplain={(val) => {
                    setSelected(val);
                    setShowModal(true);
                  }}
                  onCopy={copyHandler}
                />
              );
            })}

            <div className="mt-3">
              <Pagination
                totalCount={totalDataLength}
                currentPage={page}
                changePage={setPage}
                limit={dataSize}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
export default StrategiesMarketplace;
